import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import type { User } from '@/types/auth.ts';
import type { SalespersonStats } from '@/types/session.ts';
import type { KpiScore } from '@/types/evaluation.ts';
import { buildVendorProfileFromKpis } from '@/utils/vendorProfileBuilder.ts';
import sessionService from '@/services/sessionService.ts';
import { maestros, getMaestroName } from '@/types/maestro.ts';
import SessionDetail from '@/components/dashboard/SessionDetail.tsx';
import { VendorCardHero, KPIDetailGrid } from '@/components/vendor/index.ts';
import { useCatalogLabelMap } from '@/hooks/useCatalog.ts';
import LegalNoticeBanner from '@/components/shared/LegalNoticeBanner.tsx';
import { formatDate } from '@/utils/formatDate.ts';
import { useAsyncData } from '@/hooks/useAsyncData.ts';
import { toScore99 } from '@/utils/scoreFormat.ts';

interface MemberDetailProps {
  member: User;
  memberStats?: SalespersonStats;
  onBack: () => void;
}

export default function MemberDetail({ member, memberStats, onBack }: MemberDetailProps) {
  const { t } = useTranslation();
  const typeLabels = useCatalogLabelMap('SimulationType');
  const { data: sessions = [], loading: loadingSessions } = useAsyncData(
    () => sessionService.getTeam(1, 50).then((r) => r.items.filter((s) => s.userId === member.id)),
    [member.id],
    { initial: [] },
  );
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

  // Build vendor profile from session KPIs
  const vendorData = useMemo(() => {
    const sessionsWithReport = sessions.filter((s) => s.report);
    if (sessionsWithReport.length === 0) return null;

    // Aggregate KPI scores across all sessions
    const kpiScoreMap: Record<string, { total: number; count: number }> = {};
    for (const s of sessionsWithReport) {
      try {
        const kpis: KpiScore[] = JSON.parse(s.report!.kpiScoresJson);
        for (const kpi of kpis) {
          if (!kpiScoreMap[kpi.id]) kpiScoreMap[kpi.id] = { total: 0, count: 0 };
          kpiScoreMap[kpi.id].total += kpi.score;
          kpiScoreMap[kpi.id].count += 1;
        }
      } catch { /* ignore */ }
    }

    // Build averaged KpiScore array and delegate to the shared profile builder
    const averagedKpis: KpiScore[] = Object.entries(kpiScoreMap).map(([id, { total, count }]) => ({
      id,
      label: '',
      category: 'estructura_conversacion' as const,
      score: total / count,
      description: '',
    }));
    const profile = buildVendorProfileFromKpis(averagedKpis);
    const vendorCategories = profile.categories;
    const globalScore = profile.globalScore;

    // Get latest report strengths/improvements
    const latestReport = sessionsWithReport[0]?.report;
    let strengths: string[] = [];
    let improvements: string[] = [];
    if (latestReport) {
      try { strengths = JSON.parse(latestReport.strengthsJson); } catch { /* ignore */ }
      try { improvements = JSON.parse(latestReport.improvementsJson); } catch { /* ignore */ }
    }

    return { vendorCategories, globalScore, strengths, improvements };
  }, [sessions]);

  if (selectedSessionId) {
    return (
      <SessionDetail
        sessionId={selectedSessionId}
        onBack={() => setSelectedSessionId(null)}
        backLabel={t('admin.backToMember', { name: `${member.firstName} ${member.lastName}` })}
      />
    );
  }

  const initials = `${member.firstName[0]}${member.lastName[0]}`;
  const displayName = `${member.firstName} ${member.lastName}`;

  return (
    <div>
      {/* Back button */}
      <button
        onClick={onBack}
        className="mb-6 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('admin.backToTeam')}
      </button>

      <LegalNoticeBanner
        storageKey="disclaimer-manager-dismissed"
        messageKey="legal.managerDisclaimer"
        learnMoreLink="/data-privacy"
      />

      {/* Vendor profile or placeholder */}
      {vendorData ? (
        <>
          {/* Two-column layout: VendorCardHero + Strengths/Improvements */}
          <div className="mb-8 grid gap-6 lg:grid-cols-[280px_1fr]">
            <div>
              <VendorCardHero
                name={displayName}
                initials={initials}
                globalScore={vendorData.globalScore}
                categories={vendorData.vendorCategories}
                totalSimulations={memberStats?.sessionCount ?? sessions.length}
              />
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
                <h2 className="mb-4 font-semibold text-foreground">
                  {t('progress.strengths')}
                </h2>
                <ul className="space-y-2.5">
                  {vendorData.strengths.length > 0 ? (
                    vendorData.strengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
                        <span className="text-sm text-muted-foreground">{s}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-sm text-muted-foreground">{t('progress.noDataYet')}</li>
                  )}
                </ul>
              </div>

              <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
                <h2 className="mb-4 font-semibold text-foreground">
                  {t('progress.improvements')}
                </h2>
                <ul className="space-y-2.5">
                  {vendorData.improvements.length > 0 ? (
                    vendorData.improvements.map((s, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
                        <span className="text-sm text-muted-foreground">{s}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-sm text-muted-foreground">{t('progress.noDataYet')}</li>
                  )}
                </ul>
              </div>
            </div>
          </div>

          {/* KPI Detail Grid */}
          <div className="mb-6">
            <h2 className="mb-4 font-semibold text-foreground">{t('progress.detailedMetrics')}</h2>
            <KPIDetailGrid categories={vendorData.vendorCategories} />
          </div>
        </>
      ) : (
        <>
          {/* Member header (shown when no vendor data yet) */}
          <div className="mb-8 flex items-center gap-4 rounded-xl border border-border bg-card p-6">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xl font-bold text-primary">
              {initials}
            </div>
            <div>
              <h1 className="text-xl font-heading text-foreground">{displayName}</h1>
              <p className="text-sm text-muted-foreground">{member.email}</p>
            </div>
          </div>

          <div className="mb-8 flex flex-col items-center justify-center rounded-xl border border-border bg-muted py-12">
            <p className="text-sm font-medium text-muted-foreground">
              {t('admin.metricsAfterSessions')}
            </p>
            <p className="mt-1 text-xs text-muted-foreground/60">
              {t('admin.metricsAfterSessionsDesc')}
            </p>
          </div>
        </>
      )}

      {/* Recent activity table */}
      <div className="mb-6 rounded-xl border border-border bg-card p-4 sm:p-6">
        <h2 className="mb-4 font-semibold text-foreground">
          {t('progress.recentActivity')}
        </h2>
        {loadingSessions ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : sessions.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            {t('admin.noSessionsYet')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="whitespace-nowrap pb-2.5 pr-4 font-medium text-muted-foreground">{t('progress.tableDate')}</th>
                  <th className="whitespace-nowrap pb-2.5 pr-4 font-medium text-muted-foreground">{t('progress.tableType')}</th>
                  <th className="whitespace-nowrap pb-2.5 pr-4 font-medium text-muted-foreground">{t('progress.tableMaestro')}</th>
                  <th className="whitespace-nowrap pb-2.5 pr-4 font-medium text-muted-foreground">{t('progress.tableDuration')}</th>
                  <th className="whitespace-nowrap pb-2.5 font-medium text-muted-foreground">{t('progress.tableScore')}</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((session) => {
                  const maestro = maestros.find((m) => m.id === session.maestroId);
                  const date = session.completedAt
                    ? formatDate(session.completedAt, { day: 'numeric', month: 'short', year: 'numeric' })
                    : '-';
                  const duration = session.durationSeconds
                    ? `${Math.round(session.durationSeconds / 60)} min`
                    : '-';
                  const score = session.report?.overallScore ?? 0;

                  return (
                    <tr
                      key={session.id}
                      onClick={() => setSelectedSessionId(session.id)}
                      className="cursor-pointer border-b border-border/50 last:border-0 transition-colors hover:bg-muted"
                    >
                      <td className="whitespace-nowrap py-2.5 pr-4 text-muted-foreground">{date}</td>
                      <td className="whitespace-nowrap py-2.5 pr-4 text-foreground">{typeLabels.get(session.simulationType) ?? session.simulationType}</td>
                      <td className="whitespace-nowrap py-2.5 pr-4">
                        <span className="font-medium" style={{ color: maestro?.accentColor ?? 'var(--primary)' }}>
                          {maestro ? getMaestroName(maestro) : session.maestroId}
                        </span>
                      </td>
                      <td className="whitespace-nowrap py-2.5 pr-4 text-muted-foreground">{duration}</td>
                      <td className="py-2.5">
                        <span
                          className={`font-semibold ${
                            toScore99(score) >= 80
                              ? 'text-green-500'
                              : toScore99(score) >= 65
                                ? 'text-yellow-500'
                                : 'text-red-500'
                          }`}
                        >
                          {toScore99(score)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
